import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, authHeaders, rm, rmShort, PROJECT_COLOR } from './marketingShared';
import { CATEGORICAL } from '../constants/palette';
import './Marketing.css';

// Total Spend, Budget, Cost/Enquiry, Platforms Used — drawn from the shared
// categorical ramp so KPI tile colour matches the rest of the app.
const KPI_COLORS = [CATEGORICAL[1], CATEGORICAL[0], CATEGORICAL[4], CATEGORICAL[2]];

function MarketingSpendReport({ month }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get(`/report?month=${month}`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) { setError('Failed to load spend report'); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="mk-page"><div className="mk-error">{error}</div></div>;
  if (!data) return <div className="mk-page"><div className="mk-loading">Loading spend report…</div></div>;

  const k = data.kpis;
  const totalSpend = data.perProject.reduce((a, r) => a + r.spend, 0);
  const totalEnq = data.perProject.reduce((a, r) => a + r.enquiries, 0);
  const tgtClass = (p) => (p == null ? '' : p > 105 ? 'over' : p >= 95 ? 'on' : 'under');

  return (
    <div className="mk-page">
      <div className="mk-head">
        <div>
          <div className="mk-crumbs">Marketing Report &rsaquo; <b>Spend Report</b></div>
          <h1>Spend Report</h1>
          <div className="sub">{month} · spend by project &amp; platform, cost per enquiry, budget</div>
        </div>
        <div className="mk-head-actions">
          <button className="mk-btn primary" onClick={() => navigate('/marketing/spend')}>Key in Spend →</button>
        </div>
      </div>

      <div className="mk-banner">
        <span>ⓘ</span>
        <span><b>Read-only.</b> Actual spend rolls up from <b>Key in Spend</b>; budgets come from <b>KPI Insert</b>. Cost per enquiry blends spend against non-junk enquiries.</span>
      </div>

      {/* KPIs */}
      <div className="mk-kpis mk-kpis-four">
        <div className="mk-kpi" style={{ '--kc': KPI_COLORS[0] }}>
          <div className="k-label">Total Spend</div>
          <div className="k-val">{rmShort(k.spend)}</div>
          <div className={`k-sub ${k.budgetPct == null ? 'muted' : k.budgetPct > 100 ? 'neg' : 'pos'}`}>{k.budgetPct == null ? 'no budget set' : `${k.budgetPct}% of budget`}</div>
          <div className="k-note">Budget {k.budget > 0 ? rmShort(k.budget) : '—'}</div>
        </div>
        <div className="mk-kpi" style={{ '--kc': KPI_COLORS[1] }}>
          <div className="k-label">Budget</div>
          <div className={`k-val ${k.budget > 0 ? '' : 'muted'}`}>{k.budget > 0 ? rmShort(k.budget) : '—'}</div>
          <div className="k-sub muted">across all projects</div>
          <div className="k-note">set on Key in Spend</div>
        </div>
        <div className="mk-kpi" style={{ '--kc': KPI_COLORS[2] }}>
          <div className="k-label">Cost / Enquiry</div>
          <div className={`k-val ${k.costPerEnquiry == null ? 'muted' : ''}`}>{k.costPerEnquiry == null ? '—' : rm(k.costPerEnquiry)}</div>
          <div className="k-sub muted">blended, all projects</div>
          <div className="k-note">spend ÷ enquiries</div>
        </div>
        <div className="mk-kpi" style={{ '--kc': KPI_COLORS[3] }}>
          <div className="k-label">Platforms Used</div>
          <div className="k-val">{data.byPlatform.length}</div>
          <div className="k-sub muted">{data.byPlatform[0] ? `top: ${data.byPlatform[0].platform}` : 'none yet'}</div>
          <div className="k-note">{data.byPlatform[0] ? `${data.byPlatform[0].pct}% of spend` : '—'}</div>
        </div>
      </div>

      {/* Spend per project + target vs actual */}
      <div className="mk-row-2">
        <div className="mk-card">
          <div className="mk-card-head"><h3>Spend &amp; Cost per Enquiry — by Project</h3></div>
          <div className="mk-tbl-scroll">
            <table className="mk-tbl">
              <thead><tr><th>Project</th><th>Spend</th><th>Enquiries</th><th>Cost / Enq.</th></tr></thead>
              <tbody>
                {data.perProject.map((r) => (
                  <tr key={r.project}>
                    <td><span className="chip"><span className="dot" style={{ background: PROJECT_COLOR[r.project] }} />{r.project}</span></td>
                    <td>{rm(r.spend)}</td>
                    <td>{r.enquiries}</td>
                    <td>{r.costPerEnquiry == null ? <span className="na">—</span> : rm(r.costPerEnquiry)}</td>
                  </tr>
                ))}
                <tr className="total"><td>Total</td><td>{rm(totalSpend)}</td><td>{totalEnq}</td><td>{totalEnq > 0 ? rm(totalSpend / totalEnq) : <span className="na">—</span>}</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mk-foot"><b>Cost / Enquiry = spend ÷ enquiries</b>, per project (junk excluded).</p>
        </div>

        <div className="mk-card">
          <div className="mk-card-head"><h3>Target vs Actual — Spend Budget</h3><span className="c-sub">{month}</span></div>
          <div className="bd-list">
            {data.perProject.map((r) => (
              <div className="bd-row" key={r.project}>
                <div className="bd-top">
                  <span className="bd-name"><span className="dot" style={{ background: PROJECT_COLOR[r.project] }} />{r.project}</span>
                  <span className="bd-val"><b>{rm(r.spend)}</b> / {r.budget > 0 ? rm(r.budget) : '—'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="tgt-bar"><div className="tgt-fill" style={{ width: `${r.budget > 0 ? Math.min(100, (r.spend / r.budget) * 100) : 0}%`, background: PROJECT_COLOR[r.project] }} /><div className="tgt-mark" style={{ left: '100%' }} /></div>
                  <span className={`tgt-pct ${tgtClass(r.budgetPct)}`}>{r.budgetPct == null ? '—' : `${r.budgetPct}%`}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="mk-foot">Actual spend vs budget. Marker = 100% budget line. Green under · amber on (±5%) · red over. Set budgets on <b>KPI Insert</b>.</p>
        </div>
      </div>

      {/* Spend by platform */}
      <div className="mk-card">
        <div className="mk-card-head"><h3>Spend by Platform</h3><span className="c-sub">{month}</span></div>
        <div className="mk-tbl-scroll">
          <table className="mk-tbl">
            <thead><tr><th>Platform</th><th>Spend</th><th>% Mix</th></tr></thead>
            <tbody>
              {data.byPlatform.length ? data.byPlatform.map((r) => (
                <tr key={r.platform}><td>{r.platform}</td><td>{rm(r.spend)}</td><td>{r.pct}%</td></tr>
              )) : <tr><td className="empty" colSpan={3}>No spend logged yet.</td></tr>}
              {data.byPlatform.length > 0 && <tr className="total"><td>Total</td><td>{rm(totalSpend)}</td><td>100%</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default MarketingSpendReport;
